import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Package, ShoppingCart, Users, DollarSign, Plus, Edit, Trash2, 
  Eye, Clock, CheckCircle, Truck, XCircle, Search, Filter, Tag, Percent, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-500', label: 'Pending' },
  confirmed: { icon: CheckCircle, color: 'bg-blue-500', label: 'Confirmed' },
  preparing: { icon: Package, color: 'bg-purple-500', label: 'Preparing' },
  out_for_delivery: { icon: Truck, color: 'bg-orange-500', label: 'Out for Delivery' },
  delivered: { icon: CheckCircle, color: 'bg-green-500', label: 'Delivered' },
  cancelled: { icon: XCircle, color: 'bg-red-500', label: 'Cancelled' }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingPromo, setEditingPromo] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category: '', image_url: '', stock: '100', unit: 'each', is_featured: false
  });
  const [promoForm, setPromoForm] = useState({
    title: '', description: '', discount_percent: '', promo_code: '', image_url: '', start_date: '', end_date: '', is_active: true
  });
  const [orderFilter, setOrderFilter] = useState('all');

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchAll();
  }, [user, token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, productsRes, ordersRes, categoriesRes, promosRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/promotions?active_only=false`)
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setCategories(categoriesRes.data);
      setPromotions(promosRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: productForm.name,
        description: productForm.description,
        price: parseFloat(productForm.price) || 0,
        stock: parseInt(productForm.stock) || 0,
        category: productForm.category,
        image_url: productForm.image_url,
        unit: productForm.unit || 'each',
        is_featured: Boolean(productForm.is_featured)
      };
      
      if (!data.category) {
        toast.error('Please select a category');
        return;
      }
      
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated!');
      } else {
        await axios.post(`${API}/admin/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created!');
      }
      
      setProductDialogOpen(false);
      resetProductForm();
      fetchAll();
    } catch (error) {
      console.error('Product save error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to save product. Please check all fields.');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted!');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, 
        { order_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated!');
      fetchAll();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const resetProductForm = () => {
    setProductForm({ name: '', description: '', price: '', category: '', image_url: '', stock: '100', unit: 'each', is_featured: false });
    setEditingProduct(null);
  };

  const resetPromoForm = () => {
    setPromoForm({ title: '', description: '', discount_percent: '', promo_code: '', image_url: '', start_date: '', end_date: '', is_active: true });
    setEditingPromo(null);
  };

  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...promoForm,
        discount_percent: promoForm.discount_percent ? parseInt(promoForm.discount_percent) : null,
        featured_product_ids: []
      };
      
      if (editingPromo) {
        await axios.put(`${API}/admin/promotions/${editingPromo.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promotion updated!');
      } else {
        await axios.post(`${API}/admin/promotions`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promotion created!');
      }
      
      setPromoDialogOpen(false);
      resetPromoForm();
      fetchAll();
    } catch (error) {
      toast.error('Failed to save promotion');
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    try {
      await axios.delete(`${API}/admin/promotions/${promoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promotion deleted!');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete promotion');
    }
  };

  const openEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      title: promo.title,
      description: promo.description,
      discount_percent: promo.discount_percent?.toString() || '',
      promo_code: promo.promo_code || '',
      image_url: promo.image_url,
      start_date: promo.start_date?.split('T')[0] || '',
      end_date: promo.end_date?.split('T')[0] || '',
      is_active: promo.is_active
    });
    setPromoDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url,
      stock: product.stock.toString(),
      unit: product.unit,
      is_featured: product.is_featured
    });
    setProductDialogOpen(true);
  };

  const filteredOrders = orderFilter === 'all' 
    ? orders 
    : orders.filter(o => o.order_status === orderFilter);

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen py-8 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage your Caribbean Market</p>
          </div>
        </div>

        {/* Stats */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { icon: ShoppingCart, label: 'Total Orders', value: stats?.total_orders || 0, color: 'text-blue-500' },
              { icon: Clock, label: 'Pending Orders', value: stats?.pending_orders || 0, color: 'text-yellow-500' },
              { icon: Package, label: 'Products', value: stats?.total_products || 0, color: 'text-purple-500' },
              { icon: DollarSign, label: 'Revenue', value: `$${(stats?.total_revenue || 0).toFixed(2)}`, color: 'text-green-500' }
            ].map((stat, i) => (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <Card className="stat-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-muted-foreground text-sm">{stat.label}</p>
                        <p className="font-heading text-3xl font-bold mt-1">{stat.value}</p>
                      </div>
                      <stat.icon className={`w-10 h-10 ${stat.color}`} />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="promotions">Promotions</TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">Order Management</h2>
              <Select value={orderFilter} onValueChange={setOrderFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
              </div>
            ) : filteredOrders.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No orders found</CardContent></Card>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order) => {
                  const status = statusConfig[order.order_status];
                  const StatusIcon = status.icon;
                  return (
                    <Card key={order.id} data-testid={`admin-order-${order.id}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full ${status.color} flex items-center justify-center`}>
                              <StatusIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-medium">Order #{order.id.slice(0, 8)}</p>
                              <p className="text-sm text-muted-foreground">
                                {order.user_name} • {new Date(order.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-mono font-bold text-primary">${order.total.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">{order.items.length} items</p>
                            </div>
                            <Select 
                              value={order.order_status} 
                              onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                            >
                              <SelectTrigger className="w-[160px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(statusConfig).map(([key, config]) => (
                                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <h2 className="font-heading text-xl font-bold">Product Management ({products.length} items)</h2>
                <Input 
                  placeholder="Search products..." 
                  className="w-64"
                  onChange={(e) => {
                    const search = e.target.value.toLowerCase();
                    if (search) {
                      const filtered = products.filter(p => p.name.toLowerCase().includes(search));
                      setProducts(filtered);
                    } else {
                      fetchAll();
                    }
                  }}
                  data-testid="product-search"
                />
              </div>
              <Dialog open={productDialogOpen} onOpenChange={(open) => { setProductDialogOpen(open); if (!open) resetProductForm(); }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleProductSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required data-testid="product-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} required data-testid="product-desc-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Price ($)</Label>
                        <Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} required data-testid="product-price-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Stock</Label>
                        <Input type="number" value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })} required data-testid="product-stock-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select value={productForm.category} onValueChange={(value) => setProductForm({ ...productForm, category: value })}>
                          <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Unit</Label>
                        <Input value={productForm.unit} onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })} required data-testid="product-unit-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Image URL</Label>
                      <Input value={productForm.image_url} onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })} required data-testid="product-image-input" />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={productForm.is_featured} onCheckedChange={(checked) => setProductForm({ ...productForm, is_featured: checked })} data-testid="product-featured-switch" />
                      <Label>Featured Product</Label>
                    </div>
                    <Button type="submit" className="w-full rounded-full" data-testid="save-product-btn">
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => (
                  <Card key={product.id} data-testid={`admin-product-${product.id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <img src={product.image_url} alt={product.name} className="w-20 h-20 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{product.name}</h3>
                          <p className="text-sm text-muted-foreground">{product.category}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="font-mono font-bold text-primary">${product.price.toFixed(2)}</span>
                            {product.is_featured && <Badge>Featured</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Stock: {product.stock}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditProduct(product)} data-testid={`edit-product-${product.id}`}>
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDeleteProduct(product.id)} data-testid={`delete-product-${product.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Promotions Tab */}
          <TabsContent value="promotions" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">Promotions & Deals</h2>
              <Dialog open={promoDialogOpen} onOpenChange={(open) => { setPromoDialogOpen(open); if (!open) resetPromoForm(); }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-promo-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Promotion
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingPromo ? 'Edit Promotion' : 'Create New Promotion'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handlePromoSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input value={promoForm.title} onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })} required data-testid="promo-title-input" placeholder="e.g. Weekly Fresh Deals" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={promoForm.description} onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })} required data-testid="promo-desc-input" placeholder="Describe the promotion..." />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Discount %</Label>
                        <Input type="number" min="0" max="100" value={promoForm.discount_percent} onChange={(e) => setPromoForm({ ...promoForm, discount_percent: e.target.value })} data-testid="promo-discount-input" placeholder="e.g. 20" />
                      </div>
                      <div className="space-y-2">
                        <Label>Promo Code</Label>
                        <Input value={promoForm.promo_code} onChange={(e) => setPromoForm({ ...promoForm, promo_code: e.target.value.toUpperCase() })} data-testid="promo-code-input" placeholder="e.g. FRESH20" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date</Label>
                        <Input type="date" value={promoForm.start_date} onChange={(e) => setPromoForm({ ...promoForm, start_date: e.target.value })} required data-testid="promo-start-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input type="date" value={promoForm.end_date} onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value })} required data-testid="promo-end-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Banner Image URL</Label>
                      <Input value={promoForm.image_url} onChange={(e) => setPromoForm({ ...promoForm, image_url: e.target.value })} required data-testid="promo-image-input" placeholder="https://..." />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={promoForm.is_active} onCheckedChange={(checked) => setPromoForm({ ...promoForm, is_active: checked })} data-testid="promo-active-switch" />
                      <Label>Active</Label>
                    </div>
                    <Button type="submit" className="w-full rounded-full" data-testid="save-promo-btn">
                      {editingPromo ? 'Update Promotion' : 'Create Promotion'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
              </div>
            ) : promotions.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No promotions yet. Create your first deal!</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {promotions.map((promo) => (
                  <Card key={promo.id} className={`overflow-hidden ${!promo.is_active ? 'opacity-60' : ''}`} data-testid={`admin-promo-${promo.id}`}>
                    <div className="relative h-32">
                      <img src={promo.image_url} alt={promo.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3 text-white">
                        <h3 className="font-heading font-bold text-lg">{promo.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {promo.discount_percent && (
                            <Badge className="bg-accent text-accent-foreground">
                              <Percent className="w-3 h-3 mr-1" />{promo.discount_percent}% OFF
                            </Badge>
                          )}
                          {promo.promo_code && (
                            <Badge variant="outline" className="border-white/50 text-white">
                              <Tag className="w-3 h-3 mr-1" />{promo.promo_code}
                            </Badge>
                          )}
                          {!promo.is_active && (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{promo.description}</p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(promo.start_date).toLocaleDateString()} - {new Date(promo.end_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditPromo(promo)} data-testid={`edit-promo-${promo.id}`}>
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDeletePromo(promo.id)} data-testid={`delete-promo-${promo.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
